import { Component, ViewEncapsulation } from '@angular/core';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { BPProfilesService } from '../../bp-profiles.service';
import { Override } from '../../../../shared/app-decorators';
import { BPProfilesTerritoriesDataService, ITerritory } from './bp-profiles-territories-data.service';
import { util } from '../../../../shared/util';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import * as _ from 'underscore';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { LOV_COUNTRY, LovService } from '../../../../shared/services/lov.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { ICountry } from '../../../../../type_definitions/datatypes';
import { DialogSize, GaMessageService } from '../../../../shared/message/ga-message.service';

interface IBPProfilesTerritoriesModel {
  territories?:ITerritory[];
  addCountry?:ICountry;
}

@Component({
  selector: 'bp-profiles-territories',
  template: `
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    
    <div *ngIf="!loading">
      <p translate>BPPROFILES.TERRITORIES.COUNTRY_INFO</p>
      <formly [form]="form" [fields]="fields" [model]="model"></formly>
    </div>
  `,
  styleUrls: ['./bp-profiles-territories.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesTerritoriesComponent extends BaseBPProfilesDetailComponent {
  public model:IBPProfilesTerritoriesModel = {};

  private addCountryField:IExtraFieldConfigurationObject;
  private addCountryButtonField:IExtraFieldConfigurationObject;
  private backupTerritoriesData:ITerritory[];
  private countries:ICountry[];
  private editingMode:boolean;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private lovService:LovService,
    private bpProfilesTerritoriesDataService:BPProfilesTerritoriesDataService,
    private messageService:GaMessageService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.loading = true;
    this.bpProfilesTerritoriesDataService.getTerritories(this.bpProfilesService.activeProfile?.id)
      .then(this.onTerritoriesLoaded)
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  private onTerritoriesLoaded = (territories:ITerritory[]):void => {
    this.backupTerritoriesData = util.copyAndExtend(territories || []);
    // create formly fields and process model
    this.model.territories = territories;
    this.fields = this.createFields();
  };

  @Override(BaseBPProfilesDetailComponent)
  public onEditChange(isEdit:boolean):void {
    this.editingMode = isEdit;
    super.onEditChange(isEdit);
  }

  @Override(BaseBPProfilesDetailComponent)
  public resetModel():void {
    this.model = {
      addCountry: undefined,
      territories: util.copyAndExtend(this.backupTerritoriesData)
    };
    this.form.markAsPristine();
    this.fields = this.createFields();
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';

    return [
      this.formlyService.anArray('territories')
        .withLabelClass('d-none')
        .withValueClass(fullWidthClass)
        .withArrayFields([
          this.formlyService.aValue('country')
            .withoutWrapper()
            .withFormat(this.formattingService.formatCountry)
            .withExtraOptions({
              expressionProperties: {
                className: (model:ITerritory):string => {
                  const currentIndex:number = _.findIndex(this.model.territories, (territory:ITerritory) => territory.country?.id === model?.country?.id);
                  const evenClassName:string = currentIndex % 2 === 0 ? 'even' : '';
                  return `country-list-container ${evenClassName}`;
                }
              }
            })
            .build()
        ])
        .withExtraOptions({
          templateOptions: {
            showFieldArrayButtons: (model:ITerritory):boolean => this.editingMode && !_.isEqual(model.country?.id, this.bpProfilesService.activeProfile?.country?.id),
            addButtonLabel: 'GA.GENERAL.COUNTRY'
          }
        })
        .build(),
      this.formlyService.aGroup('addControl')
        .withoutWrapper()
        .withWrapperClass('col-12')
        .withFields([
          this.addCountryField = this.formlyService.aSelect('addCountry')
            .withoutWrapper()
            .withWrapperClass('col')
            .withFormat(this.formattingService.formatCountry)
            .withOptions(this.countries)
            .onChange(() => {
              this.addCountryButtonField.templateOptions.disabled = util.isNullOrUndefinedOrEmpty(this.model.addCountry);
            })
            .build(),
          this.addCountryButtonField = this.formlyService.aLink(this.translationService.instant('GA.BUTTONS.ADD_LABEL', { label: 'GA.GENERAL.COUNTRY' }))
            .withoutWrapper()
            .withWrapperClass('col-sm-2 d-flex align-items-center')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-primary no-margin-top'
              }
            })
            .isDisabled()
            .onClick(this.onAddCountry)
            .build()
        ])
        .withHideExpression(() => !this.editingMode)
        .build()
    ];
  }

  protected fillFields():void {
    this.formlyService.fillField(
      this.addCountryField,
      this.lovService.list(LOV_COUNTRY)
        .then((countries:ICountry[]) => {
          this.countries = countries;
          return countries;
        })
    );
  }

  private onAddCountry = ():void => {
    const existedCountryInFormModel:ITerritory[] = _.filter(this.model.territories, (territory:ITerritory):boolean => territory.country?.id === this.model.addCountry.id);
    // 1) if selected country exist in formly model then show error message that country has been added
    if (existedCountryInFormModel.length > 0) {
      this.messageService.showMessageDialog(
        'BPPROFILES.TERRITORIES.ALREADY_ADDED_TITLE',
        this.translationService.instant('BPPROFILES.TERRITORIES.ALREADY_ADDED_MESSAGE', { country: existedCountryInFormModel[0].country?.name }),
        undefined,
        undefined,
        undefined,
        DialogSize.MD
      );

      return;
    }

    // 2) if selected country exist in original model then add that one otherwise add new country
    const existedCountryInOriginalModel:ITerritory = _.find(this.backupTerritoriesData, (territory:ITerritory):boolean => territory.country.id === this.model.addCountry.id);
    this.model.territories.push(existedCountryInOriginalModel || { country: this.model.addCountry });
    this.addCountryField.formControl.reset();
    // reset fields
    this.fields = this.createFields();
  };

  private createRequestData():ITerritory[] {
    const territoriesModel:ITerritory[] = util.copyAndExtend(this.model.territories);
    // we will try to compare with original territories and set correct properties if it's removed
    _.each(this.backupTerritoriesData, (territory:ITerritory) => {
      if (!_.filter(territoriesModel, (territoryModel:ITerritory) => territory.id === territoryModel.id).length) {
        territoriesModel.push({ ...territory, remove: true });
      }
    });
    return territoriesModel;
  }

  protected onSave = ():void => {
    this.setSaveBusy(true);
    this.bpProfilesTerritoriesDataService.saveTerritories(this.bpProfilesService.activeProfile?.id, this.createRequestData())
      .then(this.onTerritoriesLoaded)
      .catch(util.noop)
      .finally(() => {
        this.setSaveBusy(false);
      });
  };
}